import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  getCustomDataLayers,
  getDataLayerDetails,
} from "../../../actions/org/OrgDataLayerActions";
import {
  selectDataLayer,
  unselectDataLayer,
} from "../../../actions/map-view/DataLayerSelectionActions";

// Constants
import { DefaultPagination } from "../../../constants/GeneralConstants";

// Components
import Spinner from "../../../components/spinner/Spinner";
import SearchInputLocal from "../../../components/search-input-local/SearchInputLocal";

// Section Components
function DataLayersList({ id, name, dataPointCountsByCity = {}, cityId }) {
  const dispatch = useDispatch();

  // Selector
  const isLayerSelected = useSelector(
    (state) => state.dataLayersSelection.isLayerSelected[id]
  );
  const isSelectionLoading = useSelector(
    (state) => state.dataLayersSelection.isSelectionLoading[id]
  );

  // city data points
  const dataPointsCount = dataPointCountsByCity[cityId] || 0;

  // functions
  function selectDataLayerFn(id) {
    // get data layer info
    if (!isLayerSelected) {
      dispatch(getDataLayerDetails(id));
    }

    // Add and Remove button config
    const dispatchFunction = isLayerSelected
      ? unselectDataLayer
      : selectDataLayer;

    // final dispatch
    dispatch(dispatchFunction(id));
  }

  // variables
  const buttonLabel = isLayerSelected ? "Added" : "Add";
  const buttonIconClassName = isLayerSelected ? "fa fa-check" : "";
  const buttonClassName = isLayerSelected
    ? "btn-outline-success"
    : "btn-outline-primary";

  return (
    <div className="navbar px-0 py-2 border-bottom">
      <label className="form-check-label ml-2" htmlFor={name}>
        {name}
      </label>
      <div>
        {isSelectionLoading && <Spinner className="spinner-grow-sm" />}
        <span className="text-muted font-italic px-2">{`Total Points: ${dataPointsCount}`}</span>
        <button
          disabled={isSelectionLoading || dataPointsCount === 0}
          className={`btn ${buttonClassName} add-button shadow-none`}
          onClick={() => selectDataLayerFn(id)}
        >
          <i className={buttonIconClassName}></i> {buttonLabel}{" "}
        </button>
      </div>
    </div>
  );
}

// get City Points Data Layers
function getCityPointsDataLayers(cityId, dataLayers = []) {
  // if no data layers present
  if (dataLayers.length === 0) {
    return [];
  }

  // filter
  const filteredDataLayers = dataLayers.filter((layer) => {
    const { dataPointCountsByCity = {} } = layer;
    return Object.keys(dataPointCountsByCity).includes(cityId);
  });

  return filteredDataLayers;
}

/**
 * Data Layer Listing Section
 */
export default function DataLayersListingSection({
  selectedViewLabels,
  cityId,
}) {
  const dispatch = useDispatch();

  // State
  const [searchedDataLayers, setSearchedDataLayers] = useState([]);

  // Selector- State
  const dataLayersLoading = useSelector(
    (state) => state.orgDataLayers.dataLayersLoading
  );
  const dataLayers = useSelector((state) => state.orgDataLayers.dataLayers);

  // city points dataLayers
  const cityPointsDataLayers = getCityPointsDataLayers(cityId, dataLayers);

  // Pagination
  const { pageNumber, pageSize } = DefaultPagination;

  useEffect(() => {
    // TODO : we need to fetch CustomDataLayers for the city only
    if (dataLayers.length < 1) {
      dispatch(getCustomDataLayers(true, "", pageNumber, pageSize));
    }
  }, [dispatch, cityId]);

  // Search Labels
  const { search } = selectedViewLabels || {};

  // Loading
  if (dataLayersLoading) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {/* Search dataLayers */}
      <SearchInputLocal
        placeholder={search.placeholder}
        dataList={cityPointsDataLayers}
        searchFields={search.fields}
        onSearchComplete={setSearchedDataLayers}
      />
      <hr className="my-2" />

      {/* Data-Layers Listing */}
      {searchedDataLayers.map((dataLayer) => {
        const { id, name, dataPointCountsByCity = {} } = dataLayer;
        return (
          <DataLayersList
            key={id}
            id={id}
            name={name}
            dataPointCountsByCity={dataPointCountsByCity}
            cityId={cityId}
          />
        );
      })}

      {/* If there is no data layer found */}
      {searchedDataLayers.length < 1 && (
        <p className="text-center mt-3 font-italic">
          {"No Data layers are available"}
        </p>
      )}
    </>
  );
}
